import { useState } from 'react';
import { apiFetch } from '../../../lib/api';
import type { EstimationType } from '../../../hooks/useMyTenant';
import EstimationCard from '../../settings/EstimationCard';

export interface EstimationPreferenceData {
  estimationType: EstimationType;
}

interface Props {
  tenantId: string;
  onStepComplete: (data: EstimationPreferenceData) => void;
  savedData?: EstimationPreferenceData;
}

export default function EstimationPreferenceStep({ tenantId, onStepComplete, savedData }: Props) {
  const [selected, setSelected] = useState<EstimationType>(savedData?.estimationType ?? 'BOTH');
  const [saving, setSaving] = useState(false);

  const handleContinue = async () => {
    setSaving(true);
    try {
      await apiFetch(`/tenants/${tenantId}`, {
        method: 'PATCH',
        body: JSON.stringify({ estimationType: selected }),
      });
      onStepComplete({ estimationType: selected });
    } catch {
      // Still continue even if save fails — can be set in Settings later
      onStepComplete({ estimationType: selected });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <h2 style={{
        fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700,
        color: 'var(--text)', marginBottom: 8,
      }}>
        How would you like estimates displayed?
      </h2>
      <p style={{
        fontFamily: 'var(--font-body)', fontSize: 14,
        color: 'var(--text-secondary)', marginBottom: 24,
      }}>
        Choose how effort estimates appear across your projects. You can change this later in Settings.
      </p>

      <EstimationCard
        tenantId={tenantId}
        initialValue={selected}
        onSaved={setSelected}
        showIntro={false}
      />

      <button
        onClick={() => void handleContinue()}
        disabled={saving}
        style={{
          fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600,
          padding: '10px 24px',
          background: 'var(--primary)', color: '#fff',
          border: 'none', borderRadius: 'var(--radius-sm)',
          cursor: saving ? 'not-allowed' : 'pointer',
          opacity: saving ? 0.6 : 1,
          marginTop: 16,
        }}
        data-testid="estimation-continue-btn"
      >
        {saving ? 'Saving...' : 'Continue'}
      </button>
    </div>
  );
}
